import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Checkbox, CheckboxProps, Col, Form, GetProp, Input, Row, Upload, UploadProps } from 'antd';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { FormProps } from 'antd/es/form/Form';

// services
import NotificationService from 'core/services/notification';
import { onCreateLanguage } from 'modules/languages/services/languages';

// components
import BackButton from 'components/backButton/backButton';
import { useTranslation } from 'react-i18next';

type FieldType = {
	name: string;
	code: string;
	flag: any;
	default: 1 | 0;
};

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

function CreateLanguagePage() {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [createForm] = Form.useForm();
	const [imageUrl, setImageUrl] = useState<string>();

	const inputStyles = {
		width: 500,
		maxWidth: '100%',
	};

	const onSubmit: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const createLanguagePayload = new FormData();
			createLanguagePayload.append('name', values.name);
			createLanguagePayload.append('code', values.code);
			createLanguagePayload.append('active', '1');
			createLanguagePayload.append('default', values.default ? '1' : ' 0');
			createLanguagePayload.append('flag', values.flag);

			await onCreateLanguage(createLanguagePayload);
			NotificationService.success(t('COMMON.SUCCESS'), t('LANGUAGES.CREATED_SUCCESS'));
			navigate('/languages');
		} catch (error: any) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.ERROR'), t('LANGUAGES.CREATE_ERROR'));
		}
	};

	const beforeUpload = (file: FileType) => {
		const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
		if (!isJpgOrPng) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.ONLY_PNG_JPG'));
			return false;
		}

		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(setImageUrl(reader.result as string));
		});

		return false;
	};

	const handleChange: UploadProps['onChange'] = (info) => {
		createForm.setFieldsValue({ flag: info.file });
	};

	const onChangeDefault: CheckboxProps['onChange'] = (e) => {
		createForm.setFieldValue('default', e.target.checked ? 1 : 0);
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
				<h3>{t('LANGUAGES.CREATE_TITLE')}</h3>
			</div>

			<div className="sectionContainer">
				<h5 style={{ marginBottom: 20 }}>{t('LANGUAGES.DETAILS')}</h5>

				<Form name="createForm" form={createForm} onFinish={onSubmit}>
					<Row gutter={[20, 20]}>
						<Col span={24} md={12}>
							<h6>{t('LANGUAGES.INFORMATION')}</h6>
							<Form.Item<FieldType> name="name" rules={[{ required: true, message: t('LANGUAGES.VALIDATION.NAME') }]}>
								<Input size="large" placeholder={t('LANGUAGES.PLACEHOLDER.NAME')} style={inputStyles} />
							</Form.Item>

							<Form.Item<FieldType> name="code" rules={[{ required: true, message: t('LANGUAGES.VALIDATION.CODE') }]}>
								<Input size="large" placeholder={t('LANGUAGES.PLACEHOLDER.CODE')} style={inputStyles} />
							</Form.Item>

							<div>
								<Form.Item<FieldType> name="default" hidden>
									<Input hidden />
								</Form.Item>
								<Checkbox onChange={onChangeDefault}>{t('LANGUAGES.DEFAULT')}</Checkbox>
							</div>
						</Col>

						<Col span={24} md={12}>
							<div>
								<h6>{t('LANGUAGES.FLAG')}</h6>
								<Form.Item<FieldType> name="flag" hidden>
									<Input />
								</Form.Item>
								<Upload
									className="squareInput"
									listType="picture-card"
									showUploadList={false}
									beforeUpload={beforeUpload}
									onChange={handleChange}>
									{imageUrl ? (
										<img src={imageUrl} alt="avatar" style={{ width: '100%' }} />
									) : (
										<button style={{ border: 0, background: 'none' }} type="button">
											{isSubmitting ? <LoadingOutlined /> : <PlusOutlined />}
											<div style={{ marginTop: 8 }}>{t('COMMON.UPLOAD')}</div>
										</button>
									)}
								</Upload>
							</div>
						</Col>
					</Row>
				</Form>

				<Form.Item>
					<Button
						size="middle"
						type="primary"
						htmlType="submit"
						form="createForm"
						loading={isSubmitting}
						style={{ marginTop: 20, display: 'flex' }}>
						{t('COMMON.ADD')}
					</Button>
				</Form.Item>
			</div>
		</>
	);
}

export default CreateLanguagePage;
